import { useRef, useState } from 'react';
import { exportJson, parsePlaces, permalink, placesFromLink } from '../lib/places';
import type { Point } from '../lib/types';

type Drawer = 'none' | 'link' | 'export' | 'import';

interface Props {
  points: Point[];
  onImport: (points: Point[]) => void;
}

/** A pasted permalink is as good as pasted JSON — accept either. */
function parsePasted(text: string): Point[] {
  const trimmed = text.trim();
  if (!trimmed) throw new Error('nothing to import — paste the JSON first');
  const fromLink = placesFromLink(trimmed);
  if (fromLink) return fromLink;
  let data: unknown;
  try {
    data = JSON.parse(trimmed);
  } catch {
    throw new Error("that isn't valid JSON — paste the whole block, braces included");
  }
  return parsePlaces(data);
}

export function PlacesIO({ points, onImport }: Props) {
  const [drawer, setDrawer] = useState<Drawer>('none');
  const [text, setText] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const areaRef = useRef<HTMLTextAreaElement | null>(null);

  const open = (which: Drawer, value: string) => {
    setDrawer(which);
    setText(value);
    setError(null);
    setNote(null);
    // Let the textarea mount before reaching for it.
    requestAnimationFrame(() => {
      const el = areaRef.current;
      if (!el) return;
      el.focus();
      if (which !== 'import') el.select();
    });
  };

  const close = () => {
    setDrawer('none');
    setText('');
    setError(null);
    setNote(null);
  };

  const share = async () => {
    const url = permalink(points);
    open('link', url);
    // The textarea is shown either way — clipboard access can be denied, and
    // over plain http it isn't there at all.
    try {
      await navigator.clipboard.writeText(url);
      setNote('Link copied to the clipboard.');
    } catch {
      setNote('Copy it from here — the clipboard was not available.');
    }
  };

  const load = () => {
    try {
      const imported = parsePasted(text);
      onImport(imported);
      setDrawer('none');
      setText('');
      setError(null);
      setNote(`Loaded ${imported.length} place${imported.length === 1 ? '' : 's'}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'could not read that');
    }
  };

  return (
    <div className="io">
      <div className="io__row">
        <button type="button" className="link" disabled={!points.length} onClick={share}>
          Copy link
        </button>
        <button
          type="button"
          className="link"
          disabled={!points.length}
          onClick={() => open('export', exportJson(points))}
        >
          Export JSON
        </button>
        <button type="button" className="link" onClick={() => open('import', '')}>
          Import
        </button>
      </div>

      {drawer !== 'none' && (
        <div className="io__drawer">
          <textarea
            ref={areaRef}
            className="io__text"
            spellCheck={false}
            rows={drawer === 'link' ? 2 : 7}
            readOnly={drawer !== 'import'}
            value={text}
            placeholder={
              drawer === 'import' ? 'Paste exported JSON — or a link someone shared — here' : undefined
            }
            onChange={(e) => setText(e.target.value)}
          />
          {error && <div className="io__error">{error}</div>}
          <div className="io__row">
            {drawer === 'import' && (
              <button type="button" className="btn btn--primary" onClick={load}>
                {points.length ? 'Replace my places' : 'Load places'}
              </button>
            )}
            <button type="button" className="btn" onClick={close}>
              {drawer === 'import' ? 'Cancel' : 'Done'}
            </button>
          </div>
        </div>
      )}

      {note && drawer !== 'import' && <div className="io__note">{note}</div>}
    </div>
  );
}
